import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { WebSpeechDetector, type Detector } from '../detect/detector';
import { getClientId } from '../lib/storage';
import { RoomClient } from '../net/room-client';
import { capOutboundVideo, getLocalMedia, MediaError_, stopStream } from '../rtc/media';
import { Peer } from '../rtc/peer';
import type { Phase, RoomSnapshot } from '@protocol';
import { initialState, opponent, reducer, you, type LifeLostFx } from './state';

export interface UseGameOptions {
  code: string;
  createDetector?: () => Detector;
  forceRelay?: boolean;
}

export function useGame({ code, createDetector, forceRelay }: UseGameOptions) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  const clientRef = useRef<RoomClient | null>(null);
  const peerRef = useRef<Peer | null>(null);
  const detectorRef = useRef<Detector | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const fxKeyRef = useRef(0);

  const snapshotRef = useRef(state.snapshot);
  snapshotRef.current = state.snapshot;

  /**
   * Signalling that arrived before there was a peer to hand it to.
   *
   * The peer connection cannot be built until the room says both players are
   * present, TURN credentials have arrived and this player has granted their
   * camera — and the opponent's offer routinely beats all three, because their
   * permission dialog and yours are independent. Dropping those frames left the
   * connection half-negotiated: the offerer had sent its one offer, so
   * `negotiationneeded` had already fired and would not fire again, and the
   * answerer was waiting for an offer that no longer existed. One side got media
   * and the other did not, and rejoining "fixed" it only because the second time
   * around the ordering happened to work out.
   *
   * So they are held instead. Order is preserved, and the queue is drained the
   * moment the peer exists.
   */
  const pendingSignalsRef = useRef<PendingSignal[]>([]);

  const applySignal = useCallback((peer: Peer, signal: PendingSignal): void => {
    if (signal.kind === 'description') void peer.onDescription(signal.type, signal.sdp);
    else void peer.onCandidate(signal.candidate);
  }, []);

  const queueSignal = useCallback(
    (signal: PendingSignal): void => {
      const peer = peerRef.current;
      if (peer) {
        applySignal(peer, signal);
        return;
      }
      // Bounded. A peer that never arrives must not grow this without limit; the
      // offer and the first candidates are what matter and they come first.
      if (pendingSignalsRef.current.length < MAX_PENDING_SIGNALS) {
        pendingSignalsRef.current.push(signal);
      }
    },
    [applySignal],
  );


  useEffect(() => {
    const detector = createDetector ? createDetector() : new WebSpeechDetector();
    detectorRef.current = detector;

    const offUtterance = detector.onUtterance((utterance) => {
      const snap = snapshotRef.current;
      if (!snap || !roundIsUnderWay(snap, clientRef.current)) return;
      clientRef.current?.send({
        type: 'speech',
        text: utterance.text,
        seq: clientRef.current.nextSeq(),
      });
    });

    const offError = detector.onError((message, fatal) => {
      if (fatal) dispatch({ type: 'detector-error', message });
      else console.warn('detector', message);
    });

    const offStatus = detector.onStatusChange((status) =>
      dispatch({ type: 'detector', status }),
    );

    detector
      .load()
      .then(() => dispatch({ type: 'detector', status: 'ready' }))
      .catch((err: unknown) => {
        dispatch({
          type: 'detector-error',
          message: err instanceof Error ? err.message : 'The speech detector failed to load.',
        });
      });

    return () => {
      offUtterance();
      offError();
      offStatus();
      detector.dispose();
      detectorRef.current = null;
    };
  }, [createDetector]);


  useEffect(() => {
    const client = new RoomClient({
      code,
      clientId: getClientId(),
      onStatus: (status) => dispatch({ type: 'socket', status }),
      onMessage: (msg) => {
        switch (msg.type) {
          case 'state':
            dispatch({ type: 'snapshot', snapshot: msg.state });
            return;

          case 'turn-credentials':
            dispatch({ type: 'ice-servers', iceServers: msg.iceServers });
            return;

          case 'words':
            dispatch({ type: 'words', yourWords: msg.yourWords, round: msg.round });
            return;

          case 'life-lost': {
            fxKeyRef.current += 1;
            const fx: LifeLostFx = {
              playerId: msg.playerId,
              word: msg.word,
              lives: msg.lives,
              showAt: msg.showAt,
              key: fxKeyRef.current,
            };
            dispatch({ type: 'fx', fx });
            dispatch({ type: 'dead', word: msg.word });
            return;
          }

          case 'offer':
          case 'answer':
            queueSignal({ kind: 'description', type: msg.type, sdp: msg.sdp });
            return;

          case 'ice-candidate':
            queueSignal({
              kind: 'candidate',
              candidate: {
                candidate: msg.candidate,
                sdpMid: msg.sdpMid,
                sdpMLineIndex: msg.sdpMLineIndex,
                usernameFragment: msg.usernameFragment,
              },
            });
            return;

          case 'error':
            if (msg.fatal) console.error('room refused the connection:', msg.message);
            return;

          default:
            return;
        }
      },
    });
    clientRef.current = client;
    client.connect();
    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [code]);


  const requestMedia = useCallback(async () => {
    if (localStreamRef.current) return;
    let stream: MediaStream;
    try {
      stream = await getLocalMedia();
    } catch (err) {
      dispatch({
        type: 'media-error',
        message:
          err instanceof MediaError_
            ? err.message
            : 'Could not start your camera and microphone.',
      });
      return;
    }
    localStreamRef.current = stream;
    setLocalStream(stream);
    dispatch({ type: 'has-media', value: true });
    dispatch({ type: 'media-error', message: null });

    try {
      await detectorRef.current?.start(stream);
    } catch (err) {
      dispatch({
        type: 'detector-error',
        message: err instanceof Error ? err.message : 'Could not start listening.',
      });
    }
  }, []);

  const askedForMediaRef = useRef(false);
  const inRoom = state.snapshot !== null;
  useEffect(() => {
    if (!inRoom) return;
    if (state.hasMedia || state.mediaError || askedForMediaRef.current) return;
    askedForMediaRef.current = true;
    void requestMedia();
  }, [inRoom, state.hasMedia, state.mediaError, requestMedia]);


  const bothPresent = (state.snapshot?.players.length ?? 0) === 2;

  useEffect(() => {
    const snap = state.snapshot;
    const stream = localStreamRef.current;
    if (!snap || !bothPresent || !stream || peerRef.current) return;
    const iceServers = state.iceServers;
    if (!iceServers) return;

    const peer = new Peer({
      polite: snap.polite,
      iceServers: iceServers as RTCIceServer[],
      forceRelay: forceRelay ?? false,
      sendOffer: (sdp) => clientRef.current?.send({ type: 'offer', sdp }),
      sendAnswer: (sdp) => clientRef.current?.send({ type: 'answer', sdp }),
      sendCandidate: (c) =>
        clientRef.current?.send({
          type: 'ice-candidate',
          candidate: c.candidate ?? '',
          sdpMid: c.sdpMid ?? null,
          sdpMLineIndex: c.sdpMLineIndex ?? null,
          usernameFragment: c.usernameFragment ?? null,
        }),
      onRemoteStream: setRemoteStream,
      onQualityChange: (quality) => dispatch({ type: 'connection', quality }),
      onNegotiated: (pc) => void capOutboundVideo(pc),
    });
    peerRef.current = peer;
    // Tracks before the queued offer, so the answer we generate describes them.
    peer.addLocalStream(stream);

    // Whatever the opponent sent while we were still getting here.
    const pending = pendingSignalsRef.current;
    pendingSignalsRef.current = [];
    for (const signal of pending) applySignal(peer, signal);

    return () => {
      peer.close();
      peerRef.current = null;
      // Anything buffered belongs to the connection that just went away.
      pendingSignalsRef.current = [];
      setRemoteStream(null);
    };
  }, [bothPresent, state.snapshot?.polite, forceRelay, state.hasMedia, state.iceServers, applySignal]);


  const [, forceTick] = useState(0);
  const startsAt = state.snapshot?.startsAt ?? null;
  const snapPhase = state.snapshot?.phase;
  useEffect(() => {
    if (snapPhase !== 'countdown' || startsAt === null) return;
    const client = clientRef.current;
    const delay = client ? client.delayUntil(startsAt) : startsAt - Date.now();
    if (delay <= 0) return;
    const timer = setTimeout(() => forceTick((n) => n + 1), delay + 30);
    return () => clearTimeout(timer);
  }, [snapPhase, startsAt]);

  const phase: Phase = state.snapshot
    ? roundIsUnderWay(state.snapshot, clientRef.current)
      ? 'playing'
      : state.snapshot.phase
    : 'waiting';


  useEffect(() => {
    const snap = state.snapshot;
    const claimAt = snap?.forfeitClaimableAt;
    if (!snap || claimAt == null || snap.phase === 'gameover') return;
    const client = clientRef.current;
    if (!client) return;
    const timer = setTimeout(
      () => client.send({ type: 'claim-forfeit' }),
      client.delayUntil(claimAt) + 250,
    );
    return () => clearTimeout(timer);
  }, [state.snapshot?.forfeitClaimableAt, state.snapshot?.phase]);


  const rematch = useCallback(() => {
    clientRef.current?.send({ type: 'rematch' });
  }, []);

  const reportFalsePositive = useCallback((word: string) => {
    clientRef.current?.send({ type: 'false-positive', word, atMs: Date.now() });
  }, []);

  const dismissFx = useCallback(() => dispatch({ type: 'fx', fx: null }), []);

  const retryConnection = useCallback(() => peerRef.current?.restart(), []);

  useEffect(
    () => () => {
      stopStream(localStreamRef.current);
      localStreamRef.current = null;
    },
    [],
  );

  const serverNow = useCallback(() => clientRef.current?.now() ?? Date.now(), []);
  const delayUntil = useCallback(
    (ts: number) => clientRef.current?.delayUntil(ts) ?? Math.max(0, ts - Date.now()),
    [],
  );

  return useMemo(
    () => ({
      state,
      you: you(state),
      opponent: opponent(state),
      localStream,
      remoteStream,
      requestMedia,
      rematch,
      reportFalsePositive,
      dismissFx,
      retryConnection,
      serverNow,
      delayUntil,
      phase,
    }),
    [
      phase,
      state,
      localStream,
      remoteStream,
      requestMedia,
      rematch,
      reportFalsePositive,
      dismissFx,
      retryConnection,
      serverNow,
      delayUntil,
    ],
  );
}

function roundIsUnderWay(snap: RoomSnapshot, client: RoomClient | null): boolean {
  if (snap.phase === 'playing') return true;
  if (snap.phase !== 'countdown' || snap.startsAt === null) return false;
  const remaining = client ? client.delayUntil(snap.startsAt) : snap.startsAt - Date.now();
  return remaining <= 0;
}

/** Signalling held until there is a peer connection to apply it to. */
type PendingSignal =
  | { kind: 'description'; type: 'offer' | 'answer'; sdp: string }
  | { kind: 'candidate'; candidate: RTCIceCandidateInit };

const MAX_PENDING_SIGNALS = 128;

export type Game = ReturnType<typeof useGame>;

export type { Detector };
